import * as logger from 'loglevel';
import dayjs from 'dayjs';
import { getUuid } from '@front10/helpers/dist/uuid';
import differenceInDays from 'date-fns/differenceInDays';
import transformRequestHistogram from './transformRequest/transformHistogram';
import axiosWrapper from '../../helpers/axiosWrapper';
import { yearAdjustmentPatch } from './yearAdjustmentPatch';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';

/**
 * Here we find the lowest fare
 */
const lowestFare = (data, blackoutDates = []) => {
  const lowest = Math.min(
    ...data
      .filter((item) => {
        if (blackoutDates.length !== 0) {
          return (
            item.fares.length > 0 &&
            item.fares[0].totalPrice &&
            !isBlackoutDate(dayjs(item.date).toDate(), blackoutDates)
          );
        }
        return item.fares.length > 0 && item.fares[0].totalPrice;
      })
      .map((value) => value.fares[0].totalPrice)
  );

  const lowerFormatted = data.find((item) => {
    const fare = item.fares[0];
    return fare && fare.totalPrice && fare.formattedTotalPrice && fare.totalPrice === lowest;
  });

  return lowerFormatted?.fares[0]?.formattedTotalPrice;
};

/**
 * The histograms API does not returns the journeyType
 * so this map allows us to use query sent parameters to do it
 */
const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

const isBlackoutDate = (date, blackoutDates) => {
  const newDate = dayjs(date);
  for (let index = 0; index < blackoutDates.length; index++) {
    const element = blackoutDates[index];
    if (
      dayjs(element.start).isSame(newDate) ||
      dayjs(element.end).isSame(newDate) ||
      (dayjs(newDate).isAfter(element.start) && dayjs(newDate).isBefore(element.end))
    ) {
      return true;
    }
  }
  return false;
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformData = (histogramRawData, filter) => {
  const { blackoutDates } = filter;
  const histogram = histogramRawData.histogram.map((item) => {
    let fareItem = item.fares && Array.isArray(item.fares) ? item.fares[0] : null;
    if (fareItem) {
      let { searchDate } = fareItem;
      // For date in safary y ie
      if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
        searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
          searchDate.length - 2
        )}`;
      }
      fareItem.searchDate = searchDate;
      //TODO:
      fareItem.shortenedFormattedTotalPrice =
        fareItem['shortenedFormattedTotalPrice '] || fareItem.shortenedFormattedTotalPrice;
      fareItem.journeyType = journeyTypeMap[fareItem.flightType];
      fareItem.dataType = 'DATA_API';
      if (fareItem.redemption && fareItem.formattedTotalPrice) {
        fareItem.redemption.formattedAmount = fareItem.formattedTotalPrice;
      }
    } else {
      fareItem = { dataType: 'EMPTY_FROM_API', departureDate: item.date };
    }
    if (blackoutDates) {
      fareItem.isBlackoutDay = isBlackoutDate(item.date, blackoutDates);
    }
    return {
      day: dayjs(item.date).toDate(),
      fareItem,
      isLowest:
        item.fares.length > 0 &&
        item.fares[0].formattedTotalPrice ===
          lowestFare(histogramRawData.histogram, blackoutDates) &&
        true
    };
  });

  //INFO: This fix was necessary since the EM API does not return the range of days specified when requesting the returnDateRange
  const { returnDateRange: { start, end } = {} } = filter;
  let differentDays = differenceInDays(new Date(end), new Date(start));
  if (start && end && differentDays !== histogram.length) {
    let dayCounter = 2;
    while (differentDays > 0) {
      const date = dayjs(new Date(start)).add(dayCounter, 'day');
      let fareItemFill = {
        day: date.toDate(),
        fareItem: { dataType: 'EMPTY_FROM_API', departureDate: date.format('YYYY-MM-DD') },
        isLowest: false
      };
      if (filter.blackoutDates) {
        fareItemFill.isBlackoutDay = isBlackoutDate(date.toDate(), filter.blackoutDates);
      }
      const found = histogram.find(
        (ele) => dayjs(ele.day).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
      );
      if (!found) {
        histogram.push(fareItemFill);
      }
      dayCounter = dayCounter + 1;
      differentDays = differentDays - 1;
    }
  }

  return histogram;
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformDataLocation = (histogramRawData, query) => {
  const locations = {};
  if (typeof histogramRawData.locations === 'object') {
    Object.keys(histogramRawData.locations).forEach((objectIndex) => {
      Object.keys(histogramRawData.locations[objectIndex]).forEach((key1) => {
        if (
          typeof histogramRawData.locations[objectIndex][key1] === 'object' &&
          histogramRawData.locations[objectIndex][key1] !== null
        ) {
          const objectValues = histogramRawData.locations[objectIndex][key1];
          Object.keys(objectValues).forEach((key2) => {
            locations[key2] = objectValues[key2];
          });
        } else if (histogramRawData.locations[objectIndex][key1] !== null) {
          // TODO: Check if is neccessary store null data
          locations[objectIndex] = histogramRawData.locations[objectIndex][key1];
        }
      });
    });
    locations.origin = query.origin;
    locations.destination = query.destination;
    locations.dataType = 'DATA_API';
    locations.destinationImage =
      locations.destinationAirportImage ||
      locations.destinationCityImage ||
      locations.destinationStateImage ||
      locations.destinationCountryImage ||
      locations.destinationRegionImage;
  } else {
    locations.dataType = 'EMPTY_FROM_API';
  }
  return locations;
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformDataByMonth = (histogramRawData, query) => {
  const histogram = [];
  const { uiDatesYearsAdjustmentPatch } = query;
  let max = 0;
  let min = Number.MAX_SAFE_INTEGER;
  let allFaresEmpty = true;

  histogramRawData.histogram.forEach((item) => {
    const fareItem = item.fares && Array.isArray(item.fares) ? item.fares[0] : null;
    if (fareItem) {
      yearAdjustmentPatch(fareItem, uiDatesYearsAdjustmentPatch);
      // Determining the highest and the lowest price in this range
      if (max < fareItem.totalPrice) {
        max = fareItem.totalPrice;
      }
      if (min > fareItem.totalPrice) {
        min = fareItem.totalPrice;
      }
      // For date in safary y ie
      let { searchDate } = fareItem;
      if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
        searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
          searchDate.length - 2
        )}`;
      }
      fareItem.searchDate = searchDate;
      fareItem.journeyType = journeyTypeMap[fareItem.flightType];
      fareItem.shortenedFormattedTotalPrice =
        fareItem['shortenedFormattedTotalPrice '] || fareItem.shortenedFormattedTotalPrice;
      fareItem.dataType = 'DATA_API';
      allFaresEmpty = false;
      histogram.push(fareItem);
    } else {
      let departureDateTransformed = item.date;
      if (uiDatesYearsAdjustmentPatch) {
        if (uiDatesYearsAdjustmentPatch < 0) {
          departureDateTransformed = dayjs(departureDateTransformed)
            .subtract(uiDatesYearsAdjustmentPatch * -1, 'year')
            .format('YYYY-MM-DD');
        } else {
          departureDateTransformed = dayjs(departureDateTransformed)
            .add(uiDatesYearsAdjustmentPatch, 'year')
            .format('YYYY-MM-DD');
        }
      }
      histogram.push({ dataType: 'EMPTY_FROM_API', departureDate: departureDateTransformed });
    }
  });

  histogram.forEach((_, objectIndex) => {
    histogram[objectIndex] = {
      ...histogram[objectIndex],
      index: getUuid(),
      isLowest:
        histogram[objectIndex].dataType === 'DATA_API' && histogram[objectIndex].totalPrice === min,
      allFaresEmpty
    };
  });
  return histogram;
};

/**
 * Makes sure the histogram response is complain with a basic schema
 * @param {*} histogram
 */
const validateHistogramResponse = (histograms) => {
  const validation = { isValid: true, message: '' };
  if (!histograms || !histograms.histogram || !Array.isArray(histograms.histogram)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: histograms,
      message: 'INTEGRATION__HISTOGRAM: Histograms must be an Array'
    };
  }
  if (histograms.length > 0) {
    const histogram = histograms[0];
    if (!histogram.origin || !histogram.destination) {
      return {
        isValid: false,
        data: histogram,
        message:
          'INTEGRATION__HISTOGRAM: Histogram SCHEMA is invalid, please check Histograms API documentation.'
      };
    }
  }
  return validation;
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getHistograms = (filter, integrationSettings, _, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v2FaresHistogram: histogramApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = histogramApiSettings;
    const filterCopy = { ...filter };
    if (
      !histogramApiSettings ||
      !histogramApiSettings.method ||
      !histogramApiSettings.endPoint ||
      !histogramApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings.histogramApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    // Format date
    filterCopy.dateRange = {
      start: dayjs(filter.dateRange.start).format('YYYY-MM-DD'),
      end: dayjs(filter.dateRange.end).format('YYYY-MM-DD')
    };
    if (filterCopy.returnDateRange !== undefined && filterCopy.returnDateRange !== null) {
      filterCopy.returnDateRange = {
        start: dayjs(filter.returnDateRange.start).format('YYYY-MM-DD'),
        end: dayjs(filter.returnDateRange.end).format('YYYY-MM-DD')
      };
    }

    delete filterCopy.originCity;
    delete filterCopy.destinationCity;

    axiosWrapper(
      {
        method: histogramApiSettings.method,
        url: `${histogramApiSettings.endPoint}`,
        headers: { ...histogramApiSettings.headers },
        data: transformRequestHistogram(filterCopy)
      },
      {
        resource: 'INTEGRATION__HISTOGRAM',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateHistogramResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
          const histograms = transformData(response.data, filterCopy);
          resolve(histograms);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getHistogramsByMonths = (filter, integrationSettings, _, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v2FaresHistogram: histogramApiSettings } = integrationSettings;
    const filterCopy = { ...filter };
    if (
      !histogramApiSettings ||
      !histogramApiSettings.method ||
      !histogramApiSettings.endPoint ||
      !histogramApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings.histogramApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    if (!filterCopy.dateRange || !filterCopy.dateRange.start || !filterCopy.dateRange.end) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **dateRange.start** or **dateRange.end** is not defined.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    // Format date
    filterCopy.dateRange = {
      start: dayjs(filter.dateRange.start).format('YYYY-MM-DD'),
      end: dayjs(filter.dateRange.end).format('YYYY-MM-DD')
    };

    delete filterCopy.originCity;
    delete filterCopy.destinationCity;

    axiosWrapper(
      {
        method: histogramApiSettings.method,
        url: `${histogramApiSettings.endPoint}`,
        headers: { ...histogramApiSettings.headers },
        data: transformRequestHistogram(filterCopy)
      },
      { resource: 'INTEGRATION__HISTOGRAM', meta, integrationSettings, pageContext }
    )
      .then((response) => {
        const validation = validateHistogramResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
          const histograms = transformDataByMonth(response.data, filter);
          const location = transformDataLocation(response.data, filter);
          resolve({ histograms, location });
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export {
  getHistograms,
  getHistogramsByMonths,
  transformData,
  transformDataLocation,
  transformDataByMonth,
  lowestFare,
  validateHistogramResponse
};
